import { z } from 'zod';
import { printCommentsSchema, printRemarksSchema } from './print-comments';
import { printHistorySchema } from './print-history';
import { printLogoSchema } from './print-image';
import { printSignaturesSchema } from './print-signatures';
import {
  barcodeSourceSchema,
  patientFieldKeySchema,
  printAlignmentSchema,
  printFontSchema,
  printFooterRepeatSchema,
  printGridSchema,
  printHeaderLayoutSchema,
  printHeaderRepeatSchema,
  printOrientationSchema,
  printPageSizeSchema,
  printTestFlowSchema,
  resultColumnKeySchema,
  withEveryResultColumn,
} from './print-options';
import { hexColor, line, mm, pt, px } from './print-fields';

/**
 * The print template — the whole shape of a printed report, as data.
 *
 * ## Why this exists
 *
 * Drawing a report at absolute millimetre coordinates, with every layout
 * decision a literal in a function, allows exactly one layout and one page
 * size, and changing a phone number on the letterhead means a deploy.
 *
 * Here the layout *is* the data. A template is a validated JSON document, the
 * renderer is a pure function of `(template, reports)`, and the builder screen
 * is a form over this schema. Adding a knob means adding a field here — not a
 * branch in a drawing routine.
 *
 * ## The one structural rule
 *
 * **Every field has a `.default()`.** A stored document is parsed on the way
 * out of the database as well as on the way in, so a template written by an
 * older version of the app gains new fields with sensible values instead of
 * failing to load. That is what makes this schema safe to extend.
 *
 * Lengths and sizes are millimetres unless the field says otherwise; font sizes
 * are PostScript points, because that is the unit type is specified in.
 */

/* ------------------------------------------------------------------------- *
 * Sections
 * ------------------------------------------------------------------------- */

/**
 * A column width.
 *
 * `'*'` shares the leftover space between every starred column, `'auto'` fits
 * the content, and a number is a fixed width in millimetres. At least one
 * column should be starred or the table will not fill the page; the renderer
 * stars the first visible column if none is.
 */
const columnWidthSchema = z.union([
  z.literal('*'),
  z.literal('auto'),
  z.coerce.number().min(5).max(200),
]);
export type ColumnWidth = z.infer<typeof columnWidthSchema>;

const resultColumnSchema = z.object({
  key: resultColumnKeySchema,
  /** The printed heading. Editable, so a lab can print "Analyte" or "Normal range". */
  label: line(),
  width: columnWidthSchema.default('auto'),
  align: printAlignmentSchema.default('left'),
  enabled: z.boolean().default(true),
});
export type ResultColumn = z.infer<typeof resultColumnSchema>;

const patientFieldSchema = z.object({
  key: patientFieldKeySchema,
  label: line(),
  enabled: z.boolean().default(true),
});
export type PatientField = z.infer<typeof patientFieldSchema>;

/**
 * One footer column — the lab's contact details, or any other fixed text.
 *
 * Identical on every sheet the template produces. The people who sign — who
 * created the report, who approved it — are the top-level `signatures`
 * section, resolved from the reports themselves.
 */
/** Lines under one footer column's heading. */
export const FOOTER_COLUMN_MAX_LINES = 6;
/** Columns across the footer. */
export const FOOTER_MAX_COLUMNS = 4;

const footerColumnSchema = z.object({
  heading: line(),
  lines: z.array(line()).max(FOOTER_COLUMN_MAX_LINES).default([]),
  align: printAlignmentSchema.default('left'),
});
export type FooterColumn = z.infer<typeof footerColumnSchema>;

const printPageSchema = z.object({
  size: printPageSizeSchema.default('A4'),
  orientation: printOrientationSchema.default('portrait'),
  /**
   * The clear space at the four paper edges.
   *
   * `top` and `bottom` are inside the header and footer bands, not on top of
   * them — see `header.band` for how the two combine.
   */
  margin_top: mm(6, 60),
  margin_right: mm(10, 60),
  margin_bottom: mm(6, 60),
  margin_left: mm(10, 60),
});
export type PrintPage = z.infer<typeof printPageSchema>;

const printThemeSchema = z.object({
  font: printFontSchema.default('Helvetica'),
  /** Body copy. Table and heading sizes are derived from this unless overridden. */
  base_size: pt(9),
  text_color: hexColor('#2d358a'),
  muted_color: hexColor('#5b6178'),
  /** The letterhead rule, the table headings and the test title. */
  accent_color: hexColor('#2d358a'),
  /** The short leading segment of the letterhead rule. */
  accent_alt_color: hexColor('#ba1c37'),
});
export type PrintTheme = z.infer<typeof printThemeSchema>;

/**
 * The patient barcode.
 *
 * Drawn as vector bars, not set in a barcode typeface. The font approach the
 * previous printer used ties bar width to a *type size*, which is the wrong
 * unit: Code 128 scanning reliability is a function of the module width in
 * millimetres, and a font that renders at 22 pt on one device and is
 * substituted on another produces a symbol that scans in the office and fails
 * at the collection desk. Bars are exact at any page size, and there is no font
 * to embed.
 */
export const printBarcodeSchema = z.object({
  enabled: z.boolean().default(true),
  source: barcodeSourceSchema.default('pin_no'),
  height: mm(11, 40),
  /**
   * The X dimension — the width of one module.
   *
   * The floor is the Code 128 specification's practical minimum for a laser
   * scanner on a 200 dpi office printer. The symbol's overall width follows
   * from this and the length of the value, which is how a barcode is supposed
   * to be sized; squeezing one to fit a box is how it stops scanning.
   */
  module_width: z.coerce.number().min(0.25).max(0.6).default(0.33),
  /** Prints the encoded value under the bars, so it can be keyed by hand. */
  show_value: z.boolean().default(true),
});
export type PrintBarcode = z.infer<typeof printBarcodeSchema>;

const printHeaderSchema = z.object({
  enabled: z.boolean().default(true),
  repeat: printHeaderRepeatSchema.default('every'),
  layout: printHeaderLayoutSchema.default('compact'),
  /**
   * Take the lab's name, letterhead lines and mark from the company profile
   * rather than from the three fields below.
   *
   * **Defaults to false, and that is deliberate.** A stored document is
   * re-parsed on the way out of the database, so defaulting this to true would
   * add the lab's name and address to every existing template on deploy — and
   * the built-in NDC letterhead sets `lab_name` to the empty string on purpose,
   * because the mark image already carries the name. Turning it on is a visible
   * choice an admin makes in the builder.
   *
   * The screens that have no template of their own — the rate list, the patient
   * slip, the printed test procedures — always read the profile directly. That
   * is where "one place to change the lab's phone number" actually pays off.
   */
  use_lab_profile: z.boolean().default(false),
  /**
   * The height of the whole top band, paper edge to first line of content.
   *
   * This is the reserved space, not the drawn height: the letterhead sits
   * inside it, inset by `page.margin_top`. It is a template field rather than
   * something measured from the content because a PDF fixes its page margins
   * for the entire document — the band cannot grow on the one page whose
   * letterhead wrapped to an extra line. The builder shows the number and the
   * preview shows the consequence.
   */
  band: mm(46, 120),
  /** Clear space at the top when the letterhead is off, for pre-printed paper. */
  blank_space: px(24),
  logo: printLogoSchema.prefault({}),
  barcode: printBarcodeSchema.prefault({}),
  /** The lab's name, set large beside the mark. Blank hides it. */
  lab_name: line(),
  lab_lines: z.array(line()).max(4).default([]),
  /** The two-tone rule under the letterhead. */
  rule: z.boolean().default(true),
  rule_accent_width: mm(30, 200),
  patient_fields: z.array(patientFieldSchema).default([]),
  /** How many columns the identity block is laid out in. */
  patient_columns: z.coerce.number().int().min(1).max(4).default(2),
});
export type PrintHeader = z.infer<typeof printHeaderSchema>;

const printStripSchema = z.object({
  enabled: z.boolean().default(true),
  text: line(),
  note: line(),
  background: hexColor('#ba1c37'),
  foreground: hexColor('#ffffff'),
});

const printPageNumberSchema = z.object({
  enabled: z.boolean().default(true),
  /** `{page}` and `{pages}` are substituted. */
  format: line('Page {page} of {pages}'),
  align: printAlignmentSchema.default('right'),
});

const printFooterSchema = z.object({
  enabled: z.boolean().default(true),
  repeat: printFooterRepeatSchema.default('every'),
  /** The height of the whole bottom band. See `header.band`. */
  band: mm(38, 120),
  /** The space left clear at the bottom when the footer is off. See `header.blank_space`. */
  blank_space: px(24),
  rule: z.boolean().default(true),
  rule_accent_width: mm(30, 200),
  /** Contact details and other fixed text. Order is left-to-right. */
  columns: z.array(footerColumnSchema).max(FOOTER_MAX_COLUMNS).default([]),
  page_number: printPageNumberSchema.prefault({}),
  strip: printStripSchema.prefault({}),
});
export type PrintFooter = z.infer<typeof printFooterSchema>;

export const printBodySchema = z.object({
  /**
   * The test name over its table.
   *
   * Never printed over a test with a single parameter: that parameter carries
   * the test's own name, and printing it twice reads as two results.
   */
  title_enabled: z.boolean().default(true),
  title_align: printAlignmentSchema.default('center'),
  title_size: pt(15),
  title_uppercase: z.boolean().default(false),

  /** Every key exactly once, in print order — see `withEveryResultColumn`. */
  columns: z.array(resultColumnSchema).default([]).transform(withEveryResultColumn),
  heading_size: pt(9),
  size: pt(9),
  /** Vertical padding inside a table cell. Drives how many rows fit on a page. */
  row_padding: z.coerce.number().min(0).max(8).default(2.5),
  grid: printGridSchema.default('horizontal'),
  grid_color: hexColor('#c9ced9'),
  /** Tints alternate rows, which helps the eye track a wide row. */
  zebra: z.boolean().default(false),
  zebra_color: hexColor('#f2f4f9'),

  /**
   * Marks a numeric result outside its recorded reference range.
   *
   * Flagging only ever reflects the range stored on the report, never the test
   * parameter's current range — the same snapshot rule the rest of the app
   * follows, so reprinting an old report cannot produce a flag nobody saw.
   */
  flag_abnormal: z.boolean().default(true),
  bold_abnormal: z.boolean().default(true),
  abnormal_color: hexColor('#ba1c37'),

  remarks: printRemarksSchema.prefault({}),
  comments: printCommentsSchema.prefault({}),

  /**
   * Previous results beside the current one.
   *
   * Nested here rather than as a top-level section because it *is* the results
   * table. `enabled` defaults to false — see the schema.
   */
  history: printHistorySchema.prefault({}),

  /** A heading naming the department above the first test of each department. */
  department_heading: z.boolean().default(true),

  test_flow: printTestFlowSchema.default('new_page'),
});
export type PrintBody = z.infer<typeof printBodySchema>;

const printWatermarkSchema = z.object({
  enabled: z.boolean().default(true),
  text: line('NDC'),
  color: hexColor('#2d358a'),
  /** 0 is invisible, 1 is solid. Anything over ~0.12 competes with the results. */
  opacity: z.coerce.number().min(0).max(1).default(0.06),
  angle: z.coerce.number().min(-90).max(90).default(-30),
  bold: z.boolean().default(true),
});
export type PrintWatermark = z.infer<typeof printWatermarkSchema>;

/* ------------------------------------------------------------------------- *
 * The document
 * ------------------------------------------------------------------------- */

export const printTemplateDocumentSchema = z.object({
  page: printPageSchema.prefault({}),
  theme: printThemeSchema.prefault({}),
  header: printHeaderSchema.prefault({}),
  body: printBodySchema.prefault({}),
  /**
   * The signatories printed at the foot of every page.
   *
   * A top-level section rather than part of `footer`: they print whether or
   * not the footer does — pre-printed stationery still needs a signature — and
   * `applyJobOverrides` merges by section, so a job can suppress them without
   * touching the footer.
   */
  signatures: printSignaturesSchema.prefault({}),
  footer: printFooterSchema.prefault({}),
  watermark: printWatermarkSchema.prefault({}),
});
export type PrintTemplateDocument = z.infer<typeof printTemplateDocumentSchema>;
